import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { FiEdit, FiTrash2, FiFileText, FiCheckCircle, FiVideo, FiRefreshCw, FiPlay, FiCheck, FiLink, FiCopy } from 'react-icons/fi';
import { b2bAgenciesAPI, activitiesAPI, meetingsAPI, agencyAgreementsAPI, settingsAPI, studentsAPI, zoomAPI, documentTemplatesAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import EmailThreadPanel from '../../components/Common/EmailThreadPanel';
import StatusBadge from '../../components/Common/StatusBadge';
import Modal from '../../components/Common/Modal';
import { toast } from 'react-toastify';
import { canMarkMeetingCompleted, meetingCompletedBlockedMessage } from '../../utils/meetingCompletion';
import { getTodayDateInputMin, isMeetingStartInFuture } from '../../utils/futureDateTime';
import { resolveDefaultTimezoneFromList } from '../../utils/browserTimezone';
import AuditTimeline from '../../components/Common/AuditTimeline';
import { getDocumentFileSizeError } from '../../utils/uploadLimits';
import { getTemplateFieldInputKind } from '../../utils/documentTemplateFields';
import { useCountry } from '../../context/CountryContext';
import { useTranslation } from 'react-i18next';

function HistoryDetails({ title, logs, filterLog }) {
  return (
    <div style={{ marginTop: 12 }}>
      <details>
        <summary
          style={{
            cursor: 'pointer',
            userSelect: 'none',
            fontWeight: 700,
            color: 'var(--text)',
            fontSize: 13
          }}
        >
          {title}
        </summary>
        <div style={{ marginTop: 10 }}>
          <AuditTimeline
            logs={logs}
            showEntity={false}
            maxCollapsedChanges={3}
            filterLog={filterLog}
          />
        </div>
      </details>
    </div>
  );
}

const MEETING_STATUSES = ['Scheduled', 'Completed', 'Cancelled', 'Rescheduled'];
const MEETING_MODES = ['Online', 'Offline'];
const TIME_ZONES = ['Asia/Colombo', 'Asia/Dubai', 'Europe/Paris', 'Europe/London', 'Asia/Kolkata', 'UTC'];

const LeadDetail = () => {
  const { t } = useTranslation();
  const { canPerformAction, user: currentUser } = useAuth();
  const { getConfig, activeCountry } = useCountry();
  const isFranceCRM = String(activeCountry || '').toUpperCase() === 'FRANCE';
  const countryConfig = getConfig();
  const requiresVerification = countryConfig.features.agreementVerification !== false;
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [lead, setLead] = useState(null);
  const [activities, setActivities] = useState([]);
  const [meetings, setMeetings] = useState([]);
  const [auditLog, setAuditLog] = useState([]);
  const [agreements, setAgreements] = useState([]);
  const [agencyStudents, setAgencyStudents] = useState([]);
  const [activeTab, setActiveTab] = useState('details');
  const [loading, setLoading] = useState(true);
  const [showActivityModal, setShowActivityModal] = useState(false);
  const [showMeetingModal, setShowMeetingModal] = useState(false);
  const [editingMeeting, setEditingMeeting] = useState(null);
  const [showAgreementModal, setShowAgreementModal] = useState(false);
  const [activityForm, setActivityForm] = useState({ activity_type: 'Call', activity_date: new Date(new Date().toLocaleString('en-US', { timeZone: 'Asia/Kolkata' })).toISOString().slice(0, 10), activity_time: '', next_followup: '', notes: '' });
  const emptyLeadMeetingForm = () => ({
    title: '', meeting_type: 'Agency Partnership', meeting_date: '', meeting_time: '', timezone: resolveDefaultTimezoneFromList(TIME_ZONES),
    mode: 'Online', location: '', meeting_link: '', notes: '', status: 'Scheduled',
    zoom_meeting_id: '', zoom_join_url: '', zoom_start_url: '', zoom_passcode: ''
  });
  const [meetingForm, setMeetingForm] = useState(emptyLeadMeetingForm);
  const [meetingTypes, setMeetingTypes] = useState([]);
  const [zoomConfigured, setZoomConfigured] = useState(false);
  const [zoomGenerating, setZoomGenerating] = useState(null);
  const [zoomGeneratingModal, setZoomGeneratingModal] = useState(false);
  const [zoomGeneratingReschedule, setZoomGeneratingReschedule] = useState(false);
  const [zoomSyncing, setZoomSyncing] = useState(null);
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const [rescheduleMeeting, setRescheduleMeeting] = useState(null);
  const [rescheduleForm, setRescheduleForm] = useState(null);
  const [updatingMeetingStatusId, setUpdatingMeetingStatusId] = useState(null);
  const [initialLeadMeetingSlot, setInitialLeadMeetingSlot] = useState(null);
  const [agreementForm, setAgreementForm] = useState({
    agreement_date: new Date(new Date().toLocaleString('en-US', { timeZone: 'Asia/Kolkata' })).toISOString().slice(0, 10),
    director_name: '',
    company_name: '',
    company_email: '',
    company_address: ''
  });
  const [editingAgreement, setEditingAgreement] = useState(null);
  const [showEditAgreementModal, setShowEditAgreementModal] = useState(false);
  const [editAgreementSaving, setEditAgreementSaving] = useState(false);
  const [editAgreementSignedFile, setEditAgreementSignedFile] = useState(null);
  const [generatedAgreement, setGeneratedAgreement] = useState(null); // { agreement_id, generated_url }
  const [regenerateAgreementId, setRegenerateAgreementId] = useState(null); // draft re-generate target
  const [modalSignedFile, setModalSignedFile] = useState(null); // File chosen in modal
  const [signedFiles, setSignedFiles] = useState({}); // Map agreementId -> File chosen in list cards
  const [uploadingSignedAgreementId, setUploadingSignedAgreementId] = useState(null);
  const [agreementGenerating, setAgreementGenerating] = useState(false);
  const [downloadingDraftAgreementId, setDownloadingDraftAgreementId] = useState(null);
  const [downloadingSignedAgreementId, setDownloadingSignedAgreementId] = useState(null);
  const [agreementTemplates, setAgreementTemplates] = useState([]);
  const [selectedAgrTemplate, setSelectedAgrTemplate] = useState(null);
  const [agrTemplateFields, setAgrTemplateFields] = useState([]);
  const [agrFieldValues, setAgrFieldValues] = useState({});
  const [generatingStudentLink, setGeneratingStudentLink] = useState(false);
  const [showStudentLinkLanguageModal, setShowStudentLinkLanguageModal] = useState(false);
  const [studentLinkLanguage, setStudentLinkLanguage] = useState('en');
  const copyToClipboard = useCallback(async (text, kind) => {
    const value = String(text || '').trim();
    if (!value || value === '-') return;
    try {
      await navigator.clipboard.writeText(value);
      toast.success(kind === 'phone' ? 'Phone number copied' : 'Email copied');
    } catch (_) {
      toast.error('Copy failed');
    }
  }, []);

  /** Resolve owner / closed-by display names for agency-linked students, including lead fallbacks when IDs align or per-student fields are unset. */
  const resolveAgencyStudentOwnerClosed = useMemo(() => {
    const userId = (v) => {
      if (v == null || v === '') return null;
      const n = Number(v);
      return Number.isFinite(n) && n > 0 ? n : null;
    };
    return (s) => {
      const lo = userId(lead?.owner_id);
      const lc = userId(lead?.closed_by);
      const so = userId(s.owner_id);
      const sc = userId(s.closed_by);

      const ownerFromApi = (s.owner_name && String(s.owner_name).trim()) || '';
      const closedFromApi = (s.closed_by_name && String(s.closed_by_name).trim()) || '';

      let owner = ownerFromApi;
      if (!owner) {
        if (so != null && lo != null && so === lo && lead?.owner_name) {
          owner = String(lead.owner_name).trim();
        } else if (so == null && lead?.owner_name) {
          owner = String(lead.owner_name).trim();
        }
      }

      let closed = closedFromApi;
      if (!closed) {
        if (sc != null && lc != null && sc === lc && lead?.closed_by_name) {
          closed = String(lead.closed_by_name).trim();
        } else if (sc == null && lead?.closed_by_name) {
          closed = String(lead.closed_by_name).trim();
        }
      }

      return {
        owner: owner || '—',
        closed: closed || '—'
      };
    };
  }, [lead]);

  const loadLead = useCallback(async () => {
    try {
      // Primary request: if this fails, the page cannot render
      const leadRes = await b2bAgenciesAPI.getById(id);
      setLead(leadRes.data);

      const leadIdParam = parseInt(id, 10);
      // Load each resource independently so one failure (e.g. audit log) does not block meetings from loading
      meetingsAPI
        .getAll({ lead_id: leadIdParam, limit: 200, page: 1 })
        .then((meetRes) => setMeetings(meetRes.data.meetings || []))
        .catch((err) => {
          // eslint-disable-next-line no-console
          console.error('Lead meetings load error:', err?.response || err);
          setMeetings([]);
        });

      activitiesAPI
        .getAll({ lead_id: id })
        .then((actRes) => setActivities(actRes.data.activities || []))
        .catch(() => setActivities([]));

      settingsAPI
        .getAuditLog({ lead_id: id, include_related: 1, limit: 200 })
        .then((auditRes) => setAuditLog(auditRes.data || []))
        .catch(() => setAuditLog([]));

      settingsAPI
        .getMeetingTypes()
        .then((meetingTypesRes) =>
          setMeetingTypes((meetingTypesRes.data || []).filter((t) => t.status === 'active'))
        )
        .catch(() => setMeetingTypes([]));

      // Agreements + linked students for B2B: load separately so one failure does not block the other
      // (previously Promise.all skipped setAgencyStudents entirely if agreements failed).
      if (leadRes.data.lead_type === 'B2B') {
        try {
          const agrRes = await agencyAgreementsAPI.getAll({ lead_id: id });
          setAgreements(agrRes.data.agreements || []);
        } catch (agrErr) {
          // eslint-disable-next-line no-console
          console.error('[LeadDetail] agreements load failed (students still load separately)', {
            leadId: id,
            status: agrErr?.response?.status,
            data: agrErr?.response?.data,
            message: agrErr?.message
          });
          setAgreements([]);
        }

        try {
          const studentsRes = await studentsAPI.getAll({ agency_id: id, student_type: 'b2b', limit: 200 });
          const payload = studentsRes.data || {};
          const linked = Array.isArray(payload.students) ? payload.students : [];
          // eslint-disable-next-line no-console
          console.log('[LeadDetail] Linked B2B students response', {
            leadId: id,
            httpStatus: studentsRes.status,
            total: payload.total,
            returned: linked.length,
            dataKeys: payload && typeof payload === 'object' ? Object.keys(payload) : []
          });
          setAgencyStudents(linked);
        } catch (stuErr) {
          // eslint-disable-next-line no-console
          console.error('[LeadDetail] linked students load failed', {
            leadId: id,
            status: stuErr?.response?.status,
            data: stuErr?.response?.data,
            message: stuErr?.message
          });
          setAgencyStudents([]);
        }

        documentTemplatesAPI.list({ type: 'agreement', status: 'active' })
          .then((tplRes) => setAgreementTemplates(tplRes.data || []))
          .catch(() => setAgreementTemplates([]));
      } else {
        setAgencyStudents([]);
        setAgreements([]);
      }
    } catch (err) {
      // Primary load failed – show backend message if available
      // eslint-disable-next-line no-console
      console.error('Lead load error:', err?.response || err);
      toast.error(err?.response?.data?.message || t('leadDetail.failedLoad'));
      navigate('/b2b-agencies');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => { loadLead(); }, [loadLead]);

  /** Debug: confirm Students tab sees the same count as API (open DevTools → Console). */
  useEffect(() => {
    if (lead?.lead_type !== 'B2B') return;
    // eslint-disable-next-line no-console
    console.log('[LeadDetail] B2B tab / linked students state', {
      leadId: id,
      activeTab,
      linkedStudentsCount: agencyStudents.length
    });
  }, [lead?.lead_type, id, activeTab, agencyStudents.length]);

  useEffect(() => {
    zoomAPI.getStatus().then((r) => setZoomConfigured(r.data.configured)).catch(() => {});
  }, []);

  const handleGenerateStudentLinkForAgency = async (languageOverride) => {
    try {
      setGeneratingStudentLink(true);
      const selectedLang = String(languageOverride || studentLinkLanguage || 'en').toLowerCase();
      const { data } = await studentsAPI.createIntakeLink({
        student_type: 'b2b',
        agency_id: Number(id),
        ...(isFranceCRM ? { form_language: (selectedLang === 'fr' ? 'fr' : 'en') } : {})
      });
      const url = String(data?.url || '').trim();
      if (!url) {
        toast.error('Could not generate link');
        return;
      }
      await navigator.clipboard.writeText(url);
      toast.success(t('students.linkCopied'));
    } catch (err) {
      const fallback = t('students.copyFailed');
      toast.error(err?.response?.data?.message || fallback);
    } finally {
      setGeneratingStudentLink(false);
      setShowStudentLinkLanguageModal(false);
    }
  };

  const formatTimeForInput = (t) => {
    if (!t) return '';
    const s = String(t);
    return s.length >= 5 ? s.slice(0, 5) : s;
  };

  useEffect(() => {
    if (!meetingTypes.length) return;
    const names = meetingTypes.map(t => t.type_name);
    if (!names.includes(meetingForm.meeting_type)) {
      const preferred = names.includes('Agency Partnership') ? 'Agency Partnership' : names[0];
      setMeetingForm(f => ({ ...f, meeting_type: preferred }));
    }
  }, [meetingTypes, meetingForm.meeting_type]);

  const handleLogActivity = async () => {
    // Basic validation to match backend requirements
    if (!activityForm.activity_type || !activityForm.activity_date || !activityForm.notes.trim()) {
      toast.error(t('leadDetail.toasts.fillActivityFields'));
      return;
    }
    try {
      await activitiesAPI.create({ ...activityForm, lead_id: id });
      toast.success(t('leadDetail.toasts.activityLogged'));
      setShowActivityModal(false);
      setActivityForm({ activity_type: 'Call', activity_date: new Date(new Date().toLocaleString('en-US', { timeZone: 'Asia/Kolkata' })).toISOString().slice(0, 10), activity_time: '', next_followup: '', notes: '' });
      loadLead();
    } catch (err) {
      toast.error(err.response?.data?.message || t('leadDetail.toasts.activityFailed'));
    }
  };

  const handleScheduleMeeting = async () => {
    if (!meetingForm.title?.trim() || !meetingForm.meeting_date || !meetingForm.meeting_time) {
      toast.error(t('leadDetail.toasts.fillMeetingFields'));
      return;
    }
    if (meetingForm.mode === 'Offline' && !meetingForm.location?.trim()) {
      toast.error(t('leadDetail.toasts.locationRequired'));
      return;
    }
    const mustBeFutureLead =
      !editingMeeting ||
      meetingForm.meeting_date !== initialLeadMeetingSlot?.date ||
      meetingForm.meeting_time !== initialLeadMeetingSlot?.time;
    if (mustBeFutureLead && !isMeetingStartInFuture(meetingForm.meeting_date, meetingForm.meeting_time, meetingForm.timezone)) {
      toast.error(t('leadDetail.toasts.futureDateRequired'));
      return;
    }
    if (
      editingMeeting &&
      meetingForm.status === 'Completed' &&
      editingMeeting.status !== 'Completed' &&
      !canMarkMeetingCompleted(editingMeeting, currentUser?.role, activeCountry)
    ) {
      toast.error(meetingCompletedBlockedMessage());
      return;
    }
    try {
      const payload = {
        ...meetingForm,
        zoom_meeting_id: meetingForm.zoom_meeting_id || null,
        zoom_join_url: meetingForm.zoom_join_url || null,
        zoom_start_url: meetingForm.zoom_start_url || null,
        zoom_passcode: meetingForm.zoom_passcode || null
      };
      if (editingMeeting?.meeting_id) {
        await meetingsAPI.update(editingMeeting.meeting_id, {
          ...payload,
          lead_id: parseInt(id, 10),
          student_id: editingMeeting.student_id || null
        });
        toast.success(t('leadDetail.toasts.meetingUpdated'));
      } else {
        const { status: _omitStatus, ...createPayload } = payload;
        await meetingsAPI.create({ ...createPayload, lead_id: parseInt(id, 10) });
        toast.success(t('leadDetail.toasts.meetingScheduled'));
      }
      setShowMeetingModal(false);
      setEditingMeeting(null);
      setInitialLeadMeetingSlot(null);
      setMeetingForm(emptyLeadMeetingForm());
      loadLead();
    } catch (err) {
      toast.error(err.response?.data?.message || t('leadDetail.toasts.meetingFailed'));
    }
  };

  const generateZoomOnLeadCard = async (meeting) => {
    if (!meeting.meeting_date || !meeting.meeting_time) {
      toast.error(t('leadDetail.toasts.setDateFirst'));
      return;
    }
    setZoomGenerating(meeting.meeting_id);
    try {
      const startTime = `${String(meeting.meeting_date).slice(0, 10)}T${formatTimeForInput(meeting.meeting_time)}:00`;
      await zoomAPI.generate({
        topic: meeting.title || 'CRM Meeting',
        start_time: startTime,
        duration: 60,
        timezone: meeting.timezone || resolveDefaultTimezoneFromList(TIME_ZONES),
        entity_type: 'meeting',
        entity_id: meeting.meeting_id
      });
      toast.success(t('leadDetail.toasts.zoomCreated'));
      loadLead();
    } catch (err) {
      toast.error(err.response?.data?.message || t('leadDetail.toasts.zoomFailed'));
    }
    setZoomGenerating(null);
  };

  const generateZoomInLeadMeetingModal = async () => {
    if (!meetingForm.meeting_date || !meetingForm.meeting_time) {
      toast.error(t('leadDetail.toasts.setDateTimeFirst'));
      return;
    }
    if (!isMeetingStartInFuture(meetingForm.meeting_date, meetingForm.meeting_time, meetingForm.timezone)) {
      toast.error(t('leadDetail.toasts.futureDateRequired'));
      return;
    }
    if (!meetingForm.title?.trim()) {
      toast.error(t('leadDetail.toasts.enterTitleFirst'));
      return;
    }
    if (meetingForm.mode !== 'Online') {
      toast.error(t('leadDetail.toasts.switchOnline'));
      return;
    }
    setZoomGeneratingModal(true);
    try {
      const startTime = `${meetingForm.meeting_date.slice(0, 10)}T${meetingForm.meeting_time}:00`;
      const payload = {
        topic: meetingForm.title.trim() || 'CRM Meeting',
        start_time: startTime,
        duration: 60,
        timezone: meetingForm.timezone || resolveDefaultTimezoneFromList(TIME_ZONES),
        entity_type: 'meeting'
      };
      if (editingMeeting?.meeting_id) {
        payload.entity_id = editingMeeting.meeting_id;
      }
      const { data } = await zoomAPI.generate(payload);
      setMeetingForm((f) => ({
        ...f,
        meeting_link: data.join_url || f.meeting_link,
        zoom_meeting_id: data.zoom_meeting_id || '',
        zoom_join_url: data.join_url || '',
        zoom_start_url: data.start_url || '',
        zoom_passcode: data.passcode || ''
      }));
      toast.success(t('leadDetail.toasts.zoomCreated'));
    } catch (err) {
      toast.error(err.response?.data?.message || t('leadDetail.toasts.zoomFailed'));
    }
    setZoomGeneratingModal(false);
  };

  const syncZoomOnLeadMeeting = async (meeting) => {
    if (!meeting.zoom_meeting_id) return;
    setZoomSyncing(meeting.meeting_id);
    try {
      await zoomAPI.sync({ entity_type: 'meeting', entity_id: meeting.meeting_id });
      toast.success(t('leadDetail.toasts.zoomSynced'));
      loadLead();
    } catch (err) {
      toast.error(err.response?.data?.message || t('leadDetail.toasts.syncFailed'));
    }
    setZoomSyncing(null);
  };

  const openLeadRescheduleModal = (meeting) => {
    setRescheduleMeeting(meeting);
    setRescheduleForm({
      meeting_date: meeting.meeting_date?.slice(0, 10) || '',
      meeting_time: formatTimeForInput(meeting.meeting_time),
      timezone: meeting.timezone || resolveDefaultTimezoneFromList(TIME_ZONES),
      mode: meeting.mode || 'Online',
      location: meeting.location || '',
      meeting_link: meeting.meeting_link || '',
      zoom_meeting_id: meeting.zoom_meeting_id || '',
      zoom_join_url: meeting.zoom_join_url || '',
      zoom_start_url: meeting.zoom_start_url || '',
      zoom_passcode: meeting.zoom_passcode || ''
    });
    setShowRescheduleModal(true);
  };

  const closeLeadRescheduleModal = () => {
    setShowRescheduleModal(false);
    setRescheduleMeeting(null);
    setRescheduleForm(null);
  };

  const generateZoomInLeadRescheduleModal = async () => {
    if (!rescheduleForm?.meeting_date || !rescheduleForm?.meeting_time) {
      toast.error(t('leadDetail.toasts.setDateTimeFirst'));
      return;
    }
    if (!isMeetingStartInFuture(rescheduleForm.meeting_date, rescheduleForm.meeting_time, rescheduleForm.timezone)) {
      toast.error(t('leadDetail.toasts.futureDateRequired'));
      return;
    }
    if (rescheduleForm.mode !== 'Online') {
      toast.error(t('leadDetail.toasts.zoomOnlineOnly'));
      return;
    }
    if (!rescheduleMeeting?.meeting_id) return;
    setZoomGeneratingReschedule(true);
    try {
      const startTime = `${rescheduleForm.meeting_date.slice(0, 10)}T${rescheduleForm.meeting_time}:00`;
      const { data } = await zoomAPI.generate({
        topic: rescheduleMeeting.title || 'CRM Meeting',
        start_time: startTime,
        duration: 60,
        timezone: rescheduleForm.timezone || resolveDefaultTimezoneFromList(TIME_ZONES),
        entity_type: 'meeting',
        entity_id: rescheduleMeeting.meeting_id
      });
      setRescheduleForm((f) => ({
        ...f,
        meeting_link: data.join_url || f.meeting_link,
        zoom_meeting_id: data.zoom_meeting_id || '',
        zoom_join_url: data.join_url || '',
        zoom_start_url: data.start_url || '',
        zoom_passcode: data.passcode || ''
      }));
      toast.success(t('leadDetail.toasts.zoomLinkUpdated'));
    } catch (err) {
      toast.error(err.response?.data?.message || t('leadDetail.toasts.zoomFailed'));
    }
    setZoomGeneratingReschedule(false);
  };

  const handleLeadRescheduleSubmit = async () => {
    if (!rescheduleForm || !rescheduleMeeting) return;
    if (!rescheduleForm.meeting_date || !rescheduleForm.meeting_time) {
      toast.error(t('leadDetail.toasts.dateTimeRequired'));
      return;
    }
    if (rescheduleForm.mode === 'Offline' && !rescheduleForm.location?.trim()) {
      toast.error(t('leadDetail.toasts.locationRequired'));
      return;
    }
    if (!isMeetingStartInFuture(rescheduleForm.meeting_date, rescheduleForm.meeting_time, rescheduleForm.timezone)) {
      toast.error(t('leadDetail.toasts.futureDateRequired'));
      return;
    }
    try {
      setUpdatingMeetingStatusId(rescheduleMeeting.meeting_id);
      await meetingsAPI.update(rescheduleMeeting.meeting_id, {
        title: rescheduleMeeting.title,
        meeting_type: rescheduleMeeting.meeting_type,
        meeting_date: rescheduleForm.meeting_date,
        meeting_time: rescheduleForm.meeting_time,
        timezone: rescheduleForm.timezone,
        mode: rescheduleForm.mode,
        location: rescheduleForm.mode === 'Offline' ? rescheduleForm.location : '',
        meeting_link: rescheduleForm.mode === 'Online' ? (rescheduleForm.meeting_link || null) : null,
        notes: rescheduleMeeting.notes,
        next_action: rescheduleMeeting.next_action,
        outcome: rescheduleMeeting.outcome,
        status: 'Rescheduled',
        lead_id: parseInt(id, 10),
        student_id: rescheduleMeeting.student_id || null,
        zoom_meeting_id: rescheduleForm.zoom_meeting_id || null,
        zoom_join_url: rescheduleForm.zoom_join_url || null,
        zoom_start_url: rescheduleForm.zoom_start_url || null,
        zoom_passcode: rescheduleForm.zoom_passcode || null
      });
      toast.success(t('leadDetail.toasts.rescheduled'));
      closeLeadRescheduleModal();
      loadLead();
    } catch (err) {
      toast.error(err.response?.data?.message || t('leadDetail.toasts.rescheduleFailed'));
    } finally {
      setUpdatingMeetingStatusId(null);
    }
  };

  const handleLeadMeetingStatusSelect = (meeting, newStatus) => {
    if (newStatus === 'Rescheduled') {
      openLeadRescheduleModal(meeting);
      return;
    }
    if (
      newStatus === 'Completed' &&
      meeting.status !== 'Completed' &&
      !canMarkMeetingCompleted(meeting, currentUser?.role, activeCountry)
    ) {
      toast.error(meetingCompletedBlockedMessage());
      return;
    }
    (async () => {
      try {
        setUpdatingMeetingStatusId(meeting.meeting_id);
        await meetingsAPI.update(meeting.meeting_id, { status: newStatus });
        toast.success(t('leadDetail.toasts.meetingUpdated'));
        loadLead();
      } catch (err) {
        toast.error(err.response?.data?.message || t('leadDetail.toasts.statusUpdateFailed'));
      } finally {
        setUpdatingMeetingStatusId(null);
      }
    })();
  };

  const handleEditMeeting = (meeting) => {
    setEditingMeeting(meeting);
    setMeetingForm({
      title: meeting.title || '',
      meeting_type: meeting.meeting_type || 'Agency Partnership',
      meeting_date: meeting.meeting_date ? String(meeting.meeting_date).slice(0, 10) : '',
      meeting_time: formatTimeForInput(meeting.meeting_time),
      timezone: meeting.timezone || resolveDefaultTimezoneFromList(TIME_ZONES),
      mode: meeting.mode || 'Online',
      location: meeting.location || '',
      meeting_link: meeting.meeting_link || '',
      notes: meeting.notes || '',
      status: meeting.status || 'Scheduled',
      zoom_meeting_id: meeting.zoom_meeting_id || '',
      zoom_join_url: meeting.zoom_join_url || '',
      zoom_start_url: meeting.zoom_start_url || '',
      zoom_passcode: meeting.zoom_passcode || ''
    });
    setInitialLeadMeetingSlot({
      date: meeting.meeting_date ? String(meeting.meeting_date).slice(0, 10) : '',
      time: formatTimeForInput(meeting.meeting_time)
    });
    setShowMeetingModal(true);
  };

  const openAgreementModal = useCallback(() => {
    setGeneratedAgreement(null);
    setRegenerateAgreementId(null);
    setModalSignedFile(null);
    setSignedFiles({});
    setSelectedAgrTemplate(null);
    setAgrTemplateFields([]);
    setAgrFieldValues({});
    if (lead) {
      const addr = [lead.city, lead.state, lead.country].filter(Boolean).join(', ');
      setAgreementForm({
        agreement_date: new Date(new Date().toLocaleString('en-US', { timeZone: 'Asia/Kolkata' })).toISOString().slice(0, 10),
        director_name: lead.decision_maker || '',
        company_name: lead.student_name || '',
        company_email: lead.email || '',
        company_address: addr
      });
    }
    setShowAgreementModal(true);
  }, [lead]);

  const openAgreementRegenerateModal = useCallback((agr) => {
    if (!agr?.agreement_id) return;
    setGeneratedAgreement(null);
    setModalSignedFile(null);
    setSignedFiles({});
    setRegenerateAgreementId(agr.agreement_id);
    setSelectedAgrTemplate(null);
    setAgrTemplateFields([]);
    setAgrFieldValues({});
    setAgreementForm({
      agreement_date: agr.agreement_date ? String(agr.agreement_date).slice(0, 10) : new Date(new Date().toLocaleString('en-US', { timeZone: 'Asia/Kolkata' })).toISOString().slice(0, 10),
      director_name: agr.director_name || '',
      company_name: agr.company_name || '',
      company_email: agr.company_email || '',
      company_address: agr.company_address || ''
    });
    setShowAgreementModal(true);
  }, []);

  const handleGenerateAgreement = async () => {
    try {
      setAgreementGenerating(true);
      const payload = { lead_id: parseInt(id) };
      if (regenerateAgreementId) {
        payload.agreement_id = regenerateAgreementId;
      }
      if (selectedAgrTemplate) {
        payload.template_id = selectedAgrTemplate.template_id;
        payload.field_values = agrFieldValues;
        payload.agreement_date =
          agrFieldValues.Agreement_Date ||
          agrFieldValues.agreement_date ||
          new Date().toISOString().slice(0, 10);
        payload.director_name =
          agrFieldValues.Director_Name ||
          agrFieldValues.director_name ||
          agrFieldValues.Partner_Signatory ||
          agrFieldValues.partner_signatory ||
          lead?.decision_maker ||
          '-';
        payload.company_name =
          agrFieldValues.Company_Name ||
          agrFieldValues.company_name ||
          agrFieldValues.Partner_Company_Name ||
          agrFieldValues.partner_company_name ||
          lead?.student_name ||
          '-';
        payload.company_email =
          agrFieldValues.Partner_Email ||
          agrFieldValues.partner_email ||
          agrFieldValues.Company_Email ||
          agrFieldValues.company_email ||
          agrFieldValues.Email ||
          lead?.email ||
          '-';
        payload.company_address =
          agrFieldValues.Partner_Address ||
          agrFieldValues.partner_address ||
          agrFieldValues.Company_Address ||
          agrFieldValues.company_address ||
          agrFieldValues.Address ||
          '-';
      } else {
        Object.assign(payload, agreementForm);
      }
      const res = await agencyAgreementsAPI.generate(payload);
      setGeneratedAgreement({ agreement_id: res.data.agreement_id, generated_url: res.data.generated_url });
      setRegenerateAgreementId(null);
      setModalSignedFile(null);
      toast.success(
        res.data.regenerated
          ? t('leadDetail.toasts.agreementRegenerated')
          : t('leadDetail.toasts.agreementGenerated')
      );
      loadLead();
    } catch (err) {
      toast.error(err.response?.data?.message || t('leadDetail.toasts.agreementFailed'));
    } finally {
      setAgreementGenerating(false);
    }
  };

  const handleSelectAgrTemplate = async (tpl) => {
    setSelectedAgrTemplate(tpl);
    try {
      const res = await documentTemplatesAPI.getAutoFill({
        template_id: tpl.template_id,
        entity_type: 'lead',
        entity_id: id
      });
      setAgrTemplateFields(res.data);
      const vals = {};
      res.data.forEach(f => { vals[f.field_key] = f.value || ''; });
      setAgrFieldValues(vals);
    } catch {
      const vals = {};
      (tpl.fields || []).forEach(f => { vals[f.field_key] = ''; });
      setAgrTemplateFields((tpl.fields || []).map(f => ({
        field_key: f.field_key, field_label: f.field_label, auto_map: f.auto_map, value: ''
      })));
      setAgrFieldValues(vals);
    }
  };

  const handleUploadSignedAgreement = async (agreementId, file) => {
    if (!file) {
      toast.error(t('leadDetail.toasts.chooseSignedFile'));
      return;
    }
    const sizeErr = getDocumentFileSizeError(file);
    if (sizeErr) {
      toast.error(sizeErr);
      return;
    }
    try {
      setUploadingSignedAgreementId(agreementId);
      const formData = new FormData();
      formData.append('file', file);
      const uploadRes = await agencyAgreementsAPI.uploadSigned(agreementId, formData);
      const autoVerified = uploadRes.data?.auto_verified;
      toast.success(autoVerified
        ? t('leadDetail.toasts.signedUploadedTiedUp')
        : t('leadDetail.toasts.signedUploadedVerification'));
      setGeneratedAgreement(null);
      setModalSignedFile(null);
      setSignedFiles({});
      setShowAgreementModal(false);
      setAgreementForm({
        agreement_date: new Date(new Date().toLocaleString('en-US', { timeZone: 'Asia/Kolkata' })).toISOString().slice(0, 10),
        director_name: '', company_name: '', company_email: '', company_address: ''
      });
      loadLead();
    } catch (err) {
      toast.error(err.response?.data?.message || t('leadDetail.toasts.signedUploadFailed'));
    } finally {
      setUploadingSignedAgreementId(null);
    }
  };

  const handleDownloadGenerated = async (agreementId) => {
    try {
      setDownloadingDraftAgreementId(agreementId);
      const res = await agencyAgreementsAPI.getGeneratedUrl(agreementId);
      window.open(res.data.url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      toast.error(err.response?.data?.message || t('leadDetail.toasts.downloadFailed'));
    } finally {
      setDownloadingDraftAgreementId(null);
    }
  };

  const handleDownloadSigned = async (agreementId) => {
    try {
      setDownloadingSignedAgreementId(agreementId);
      const res = await agencyAgreementsAPI.getSignedUrl(agreementId);
      window.open(res.data.url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      toast.error(err.response?.data?.message || t('leadDetail.toasts.downloadSignedFailed'));
    } finally {
      setDownloadingSignedAgreementId(null);
    }
  };

  const openEditAgreementModal = (agreement) => {
    setEditingAgreement(agreement);
    setAgreementForm({
      agreement_date: agreement.agreement_date ? String(agreement.agreement_date).slice(0, 10) : '',
      director_name: agreement.director_name || '',
      company_name: agreement.company_name || '',
      company_email: agreement.company_email || '',
      company_address: agreement.company_address || ''
    });
    setEditAgreementSignedFile(null);
    setShowEditAgreementModal(true);
  };

  const handleSaveEditedAgreement = async () => {
    if (!editingAgreement?.agreement_id) return;
    if (!agreementForm.agreement_date || !agreementForm.director_name || !agreementForm.company_name || !agreementForm.company_email || !agreementForm.company_address) {
      toast.error(t('leadDetail.toasts.fillAgreementFields'));
      return;
    }
    try {
      setEditAgreementSaving(true);
      await agencyAgreementsAPI.update(editingAgreement.agreement_id, { ...agreementForm });
      if (editAgreementSignedFile) {
        const sizeErr = getDocumentFileSizeError(editAgreementSignedFile);
        if (sizeErr) {
          toast.error(sizeErr);
          setEditAgreementSaving(false);
          return;
        }
        const fd = new FormData();
        fd.append('file', editAgreementSignedFile);
        const reUploadRes = await agencyAgreementsAPI.uploadSigned(editingAgreement.agreement_id, fd);
        const reAutoVerified = reUploadRes.data?.auto_verified;
        toast.success(reAutoVerified
          ? t('leadDetail.toasts.agreementUpdatedTiedUp')
          : t('leadDetail.toasts.agreementUpdatedVerification'));
      } else {
        toast.success(t('leadDetail.toasts.agreementUpdated'));
      }
      setShowEditAgreementModal(false);
      setEditingAgreement(null);
      setEditAgreementSignedFile(null);
      loadLead();
    } catch (err) {
      toast.error(err.response?.data?.message || t('leadDetail.toasts.agreementUpdateFailed'));
    } finally {
      setEditAgreementSaving(false);
    }
  };

  const handleUpdateOutcome = async (meetingId, outcome) => {
    try {
      await meetingsAPI.update(meetingId, { outcome });
      toast.success(t('leadDetail.toasts.outcomeUpdated'));
      loadLead();
    } catch (err) {
      toast.error(t('leadDetail.toasts.outcomeUpdateFailed'));
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(t('leadDetail.toasts.deleteConfirm'))) return;
    try {
      await b2bAgenciesAPI.delete(id);
      toast.success(t('leadDetail.toasts.deleted'));
      navigate('/b2b-agencies');
    } catch (err) {
      toast.error(err.response?.data?.message || t('leadDetail.toasts.deleteFailed'));
    }
  };

  // ----- Sticky tabs via URL (?tab=activities) -----
  // These hooks must be declared before any conditional return.
  const isB2B = lead?.lead_type === 'B2B';
  const tabList = useMemo(() => {
    return isB2B
      ? ['details', 'meetings', 'emails', 'agreements', 'history', 'activities', 'students']
      : ['details', 'meetings', 'emails', 'activities', 'notes', 'history'];
  }, [isB2B]);

  const setTab = (tab) => {
    setActiveTab(tab);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (tab) next.set('tab', tab);
      else next.delete('tab');
      return next;
    }, { replace: true });
  };

  useEffect(() => {
    if (!lead) return;
    const requested = searchParams.get('tab');
    if (!requested) return;
    if (!tabList.includes(requested)) return;
    if (activeTab === requested) return;
    setActiveTab(requested);
  }, [lead, searchParams, tabList, activeTab]);

  if (loading) return <div className="loading-spinner"><div className="spinner"></div></div>;
  if (!lead) return <div className="empty-state"><p>{t('leadDetail.notFound')}</p></div>;

  const isB2BS = false;
  const canConvert = false;
  const canCreateAgreement = isB2B;
  const hasAnyAgreement = agreements.length > 0;
  const canGenerateAgreement = canCreateAgreement && !hasAnyAgreement;
  const latestAgreement = agreements.length > 0
    ? [...agreements].sort((a, b) => new Date(b.created_at || b.agreement_date || 0) - new Date(a.created_at || a.agreement_date || 0))[0]
    : null;
  const isAdmin = ['super_admin', 'admin', 'manager'].includes(String(currentUser?.role || '').toLowerCase());

  // B2B status badge color helper
  const getB2BStatusColor = (status) => {
    const colors = {
      'New': { bg: 'var(--info-light)', color: 'var(--info)' },
      'Contacted': { bg: '#E0E7FF', color: '#4338CA' },
      'Proposal Sent': { bg: '#FEF3C7', color: '#D97706' },
      'Negotiation': { bg: '#DBEAFE', color: '#2563EB' },
      'Tied-Up': { bg: '#D1FAE5', color: '#059669' },
      'Agreement Done': { bg: '#ECFDF5', color: '#047857' },
      'Not Interested': { bg: '#FEE2E2', color: '#DC2626' },
      'Closed': { bg: '#F3F4F6', color: '#6B7280' }
    };
    return colors[status] || { bg: '#F3F4F6', color: '#6B7280' };
  };

  return (
    <div>
      {/* Header */}
      <div className="detail-header">
        <div className="detail-left">
          <h2>{lead.student_name}</h2>
          <div className="detail-meta">
            <StatusBadge status={lead.lead_type} />
            {isB2B ? (
              <span style={{
                padding: '3px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600,
                background: getB2BStatusColor(lead.status).bg,
                color: getB2BStatusColor(lead.status).color
              }}>
                {lead.status}
              </span>
            ) : (
              <StatusBadge status={lead.status} />
            )}
            <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>{lead.ref_number}</span>
          </div>
        </div>
        <div className="detail-right">
          <div className="detail-owner">
            <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>{t('leadDetail.ownerLabel')}</span>
            <strong>{lead.owner_name || t('leadDetail.unassigned')}</strong>
          </div>
          <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>{t('leadDetail.createdLabel')} {new Date(lead.created_at).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' })}</div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {canPerformAction('b2b_agencies', 'update') && <button className="btn btn-sm btn-outline" onClick={() => navigate(`/b2b-agencies/${id}/edit`)}><FiEdit /> {t('common.edit')}</button>}
            {canGenerateAgreement && (
              <button className="btn btn-sm btn-success" onClick={openAgreementModal}><FiFileText /> {t('leadDetail.createAgreement')}</button>
            )}
            {canPerformAction('b2b_agencies', 'delete') && lead.status !== 'Agreement Done' && (
              <button className="btn btn-sm btn-danger" onClick={handleDelete}><FiTrash2 /> {t('common.delete')}</button>
            )}
          </div>
          {lead.status === 'Converted' && lead.converted_student_id && (
            <button className="btn btn-sm btn-outline" onClick={() => navigate(`/students/${lead.converted_student_id}`)}>
              {t('leadDetail.viewStudentProfile')}
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="tabs">
        {tabList.map(tab => (
          <button key={tab} className={activeTab === tab ? 'active' : ''} onClick={() => setTab(tab)}>
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'details' && (
        <div className="card">
          <div className="detail-info-grid">
            <div className="detail-info-item"><label>{t('leadDetail.refNumber')}</label><span>{lead.ref_number}</span></div>
            <div className="detail-info-item"><label>{t('leadDetail.leadType')}</label><span><StatusBadge status={lead.lead_type} /></span></div>
            <div className="detail-info-item">
              <label>{isB2B ? t('leadDetail.agencyName') : t('common.name')}</label>
              <span>{lead.student_name}</span>
            </div>
            <div className="detail-info-item">
              <label>{t('leadDetail.contact')}</label>
              <span style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
                {lead.contact_number ? (
                  <button
                    type="button"
                    onClick={() => copyToClipboard(`${lead.phone_country_code || ''} ${lead.contact_number || ''}`.trim(), 'phone')}
                    style={{ background: 'none', border: 'none', padding: 0, margin: 0, color: 'var(--text)', fontSize: 14, display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}
                    title="Copy phone number"
                  >
                    <span>{`${lead.phone_country_code || ''} ${lead.contact_number || ''}`.trim()}</span>
                    <FiCopy size={13} />
                  </button>
                ) : '-'}
                {canPerformAction('b2b_agencies', 'read') && lead.contact_number && (
                  <Link
                    to={`/b2b-agencies?phone=${encodeURIComponent(String(lead.contact_number).replace(/\D/g, ''))}`}
                    style={{ fontSize: 12, fontWeight: 500 }}
                  >
                    {t('leadDetail.searchByPhone')}
                  </Link>
                )}
              </span>
            </div>
            <div className="detail-info-item"><label>{isB2B ? t('leadDetail.locationCountry') : t('common.country')}</label><span>{lead.country || '-'}</span></div>
            {isB2B && <div className="detail-info-item"><label>{t('leadDetail.locationState')}</label><span>{lead.state || '-'}</span></div>}
            {isB2B && <div className="detail-info-item"><label>{t('leadDetail.locationCity')}</label><span>{lead.city || '-'}</span></div>}
            <div className="detail-info-item">
              <label>{t('common.email')}</label>
              <span style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
                {lead.email ? (
                  <button
                    type="button"
                    onClick={() => copyToClipboard(lead.email, 'email')}
                    style={{ background: 'none', border: 'none', padding: 0, margin: 0, color: 'var(--text)', fontSize: 14, display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}
                    title="Copy email"
                  >
                    <span>{lead.email}</span>
                    <FiCopy size={13} />
                  </button>
                ) : '-'}
                {canPerformAction('b2b_agencies', 'read') && lead.email && (
                  <Link
                    to={`/b2b-agencies?email=${encodeURIComponent(String(lead.email).trim())}`}
                    style={{ fontSize: 12, fontWeight: 500 }}
                  >
                    {t('leadDetail.searchByEmail')}
                  </Link>
                )}
              </span>
            </div>
            {isB2BS && <div className="detail-info-item"><label>{t('leadDetail.relatedAgency')}</label><span>{lead.agency_name || '-'}</span></div>}
            {!isB2B && <div className="detail-info-item"><label>{t('common.course')}</label><span>{lead.course_name || '-'}</span></div>}
            {!isB2B && <div className="detail-info-item"><label>{t('common.campus')}</label><span>{lead.campus_name || '-'}</span></div>}
            {!isB2B && <div className="detail-info-item"><label>{t('leadDetail.passportNo')}</label><span>{lead.passport_no || '-'}</span></div>}
            {!isB2B && <div className="detail-info-item"><label>{t('common.gender')}</label><span>{lead.gender || '-'}</span></div>}
            {!isB2B && <div className="detail-info-item"><label>{t('leadDetail.intake')}</label><span>{lead.intake_name || '-'}</span></div>}
            <div className="detail-info-item"><label>{t('common.source')}</label><span>{lead.source}</span></div>
            <div className="detail-info-item"><label>{isB2B ? t('leadDetail.contactPerson') : t('leadDetail.decisionMaker')}</label><span>{lead.decision_maker || '-'}</span></div>
            {!isB2B && <div className="detail-info-item"><label>{t('leadDetail.fundedBy')}</label><span>{lead.funded_by || '-'}</span></div>}
            {!isB2B && <div className="detail-info-item"><label>{t('leadDetail.inquiryBy')}</label><span>{lead.inquiry_by || '-'}</span></div>}
            <div className="detail-info-item">
              <label>Status</label>
              <span>
                {isB2B ? (
                  <span style={{
                    padding: '3px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600,
                    background: getB2BStatusColor(lead.status).bg,
                    color: getB2BStatusColor(lead.status).color
                  }}>
                    {lead.status}
                  </span>
                ) : (
                  <StatusBadge status={lead.status} />
                )}
              </span>
            </div>
            <div className="detail-info-item"><label>{t('common.owner')}</label><span>{lead.owner_name || '-'}</span></div>
            <div className="detail-info-item"><label>{t('leadDetail.closedBy')}</label><span>{lead.closed_by_name || '-'}</span></div>
            <div className="detail-info-item" style={{ gridColumn: '1 / -1' }}>
              <label>{t('common.remarks')}</label>
              <span style={{ whiteSpace: 'pre-wrap', color: 'var(--text-secondary)' }}>
                {lead.remarks != null && String(lead.remarks).trim() !== '' ? lead.remarks : '—'}
              </span>
            </div>
            <div className="detail-info-item"><label>{t('leadDetail.recordCreated')}</label><span>{lead.created_at ? new Date(lead.created_at).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' }) : '—'}</span></div>
            <div className="detail-info-item"><label>{t('leadDetail.lastUpdated')}</label><span>{lead.updated_at ? new Date(lead.updated_at).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' }) : '—'}</span></div>
          </div>

          {/* B2B Info Banner */}
          {isB2B && (
            <div style={{
              marginTop: 16, padding: 16,
              background: lead.status === 'Agreement Done' || lead.status === 'Tied-Up' ? 'var(--success-light)' : 'var(--info-light)',
              borderRadius: 'var(--radius)', border: `1px solid ${lead.status === 'Agreement Done' || lead.status === 'Tied-Up' ? 'var(--success)' : 'var(--info)'}`
            }}>
              {lead.status === 'Agreement Done' && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <FiCheckCircle style={{ color: 'var(--success)', fontSize: 20 }} />
                  <div>
                    <strong style={{ color: 'var(--success)' }}>{t('leadDetail.tiedUpBanner')}</strong>
                    <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--text-secondary)' }}>
                      {t('leadDetail.tiedUpBannerDesc')}
                    </p>
                  </div>
                </div>
              )}
              {lead.status === 'Tied-Up' && (
                <div>
                  <strong style={{ color: 'var(--success)' }}>{t('leadDetail.tiedUpNeedAgreement')}</strong>
                  <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--text-secondary)' }}>
                    {t('leadDetail.tiedUpNeedAgreementDesc')}
                  </p>
                </div>
              )}
              {!['Agreement Done', 'Tied-Up'].includes(lead.status) && (
                <div>
                  <strong>{t('leadDetail.partnershipStatus', { status: lead.status })}</strong>
                  <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--text-secondary)' }}>
                    {t('leadDetail.partnershipStatusDesc')}
                  </p>
                </div>
              )}
            </div>
          )}

          {isB2B && latestAgreement && (
            <div style={{ marginTop: 16, padding: 16, background: 'var(--bg-secondary)', borderRadius: 'var(--radius)', border: '1px solid var(--border)' }}>
              <h3 style={{ margin: 0, marginBottom: 12, fontSize: 16, fontWeight: 700 }}>{t('leadDetail.agencyBasicDetails')}</h3>
              <div className="detail-info-grid" style={{ gap: '8px 24px' }}>
                <div className="detail-info-item">
                  <label>{t('leadDetail.agreementDate')}</label>
                  <span>{latestAgreement.agreement_date ? new Date(latestAgreement.agreement_date).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' }) : '-'}</span>
                </div>
                <div className="detail-info-item">
                  <label>{t('leadDetail.directorName')}</label>
                  <span>{latestAgreement.director_name || '-'}</span>
                </div>
                <div className="detail-info-item">
                  <label>{t('leadDetail.companyName')}</label>
                  <span>{latestAgreement.company_name || '-'}</span>
                </div>
                <div className="detail-info-item">
                  <label>{t('leadDetail.companyEmail')}</label>
                  <span>{latestAgreement.company_email || '-'}</span>
                </div>
                <div className="detail-info-item">
                  <label>{t('leadDetail.companyAddress')}</label>
                  <span>{latestAgreement.company_address || '-'}</span>
                </div>
                <div className="detail-info-item">
                  <label>{t('leadDetail.agreementStatus')}</label>
                  <span>{latestAgreement.status || '-'}</span>
                </div>
              </div>
            </div>
          )}

          {lead.contributors && lead.contributors.length > 0 && (
            <div style={{ marginTop: 16 }}>
              <h3 style={{ fontSize: 16, marginBottom: 8, fontWeight: 700 }}>{t('leadDetail.contributors')}</h3>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {lead.contributors.map((c, i) => (
                  <span key={i} className="badge badge-new" style={{ fontSize: 12 }}>
                    {c.name} ({c.role})
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Recent History (compact) */}
          <div style={{ marginTop: 12 }}>
            <details>
              <summary
                style={{
                  cursor: 'pointer',
                  userSelect: 'none',
                  fontWeight: 700,
                  color: 'var(--text)',
                  fontSize: 13
                }}
              >
                {t('leadDetail.recentHistory')}
              </summary>
              <div style={{ marginTop: 10 }}>
                <AuditTimeline
                  logs={auditLog}
                  showEntity={false}
                  maxCollapsedChanges={3}
                />
              </div>
            </details>
          </div>
        </div>
      )}

      {activeTab === 'activities' && (
        <div>
          <div style={{ marginBottom: 16 }}>
            <button className="btn btn-primary btn-sm" onClick={() => setShowActivityModal(true)}>{t('leadDetail.logActivity')}</button>
          </div>
          <div className="timeline">
            {activities.map(a => (
              <div className="timeline-item" key={a.activity_id}>
                <div className="timeline-header">
                  <div className="timeline-type">
                    <StatusBadge status={a.activity_type} />
                  </div>
                  <div className="timeline-date">{new Date(a.activity_date).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' })} {a.activity_time}</div>
                </div>
                <div className="timeline-notes">{a.notes}</div>
                <div className="timeline-meta">
                  {a.next_followup && <span>Next follow-up: {new Date(a.next_followup).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' })}</span>}
                  <span>By: {a.created_by_name}</span>
                </div>
              </div>
            ))}
            {activities.length === 0 && <div className="empty-state"><p>{t('leadDetail.noActivities')}</p></div>}
          </div>

          <HistoryDetails
            title={t('leadDetail.activitiesHistory')}
            logs={auditLog}
            filterLog={(l) => String(l?.entity_type || '').toLowerCase() === 'activity'}
          />
        </div>
      )}

      {activeTab === 'students' && isB2B && (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <h3 style={{ margin: 0, fontWeight: 700 }}>{t('leadDetail.linkedStudents')} ({agencyStudents.length})</h3>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              {canPerformAction('students', 'create') && (
                <button
                  className="btn btn-sm btn-outline"
                  onClick={() => {
                    if (isFranceCRM) {
                      setStudentLinkLanguage('en');
                      setShowStudentLinkLanguageModal(true);
                    } else {
                      handleGenerateStudentLinkForAgency();
                    }
                  }}
                  disabled={generatingStudentLink}
                  title="Generate B2B student link for this agency"
                >
                  <FiLink /> {generatingStudentLink ? t('common.loading') : t('students.studentFormLink')}
                </button>
              )}
              <button
                className="btn btn-sm btn-primary"
                onClick={() => navigate(`/students/new?student_type=b2b&agency_id=${id}&lock_agency=1`)}
              >
                {t('leadDetail.addStudentLead')}
              </button>
            </div>
          </div>
          <div className="table-responsive">
            <table style={{ width: '100%', minWidth: 700 }}>
              <thead>
                <tr>
                  <th>{t('leadDetail.studentRef')}</th>
                  <th>{t('leadDetail.studentName')}</th>
                  <th>{t('leadDetail.studentContact')}</th>
                  <th>{t('leadDetail.studentStatus')}</th>
                  <th>{t('leadDetail.studentCampus')}</th>
                  <th>{t('common.owner')}</th>
                  <th>{t('leadDetail.closedBy')}</th>
                  <th>{t('leadDetail.studentAction')}</th>
                </tr>
              </thead>
              <tbody>
                {agencyStudents.map((s) => {
                  const oc = resolveAgencyStudentOwnerClosed(s);
                  return (
                  <tr key={s.student_id}>
                    <td>{s.student_ref || '-'}</td>
                    <td>{`${s.first_name || ''} ${s.last_name || ''}`.trim() || '-'}</td>
                    <td>{`${s.phone_country_code || ''} ${s.phone || ''}`.trim() || '-'}</td>
                    <td><StatusBadge status={s.status} /></td>
                    <td>{s.campus_name || '-'}</td>
                    <td>{oc.owner}</td>
                    <td>{oc.closed}</td>
                    <td>
                      <button className="btn btn-sm btn-outline" onClick={() => navigate(`/students/${s.student_id}`)}>
                        {t('leadDetail.viewStudent')}
                      </button>
                    </td>
                  </tr>
                  );
                })}
                {agencyStudents.length === 0 && (
                  <tr>
                    <td colSpan="8" style={{ textAlign: 'center', padding: 24, color: 'var(--text-muted)' }}>
                      {t('leadDetail.noStudents')}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div style={{ padding: '0 20px 16px' }}>
            <HistoryDetails
              title={t('leadDetail.studentsHistory')}
              logs={auditLog}
              filterLog={(l) => {
                const entity = String(l?.entity_type || '').toLowerCase();
                return entity === 'student' || entity === 'application';
              }}
            />
          </div>
        </div>
      )}

      {activeTab === 'meetings' && (
        <div>
          <div style={{ marginBottom: 16 }}>
            <button
              className="btn btn-primary btn-sm"
              onClick={() => {
                setEditingMeeting(null);
                setInitialLeadMeetingSlot(null);
                setMeetingForm(emptyLeadMeetingForm());
                setShowMeetingModal(true);
              }}
            >
              {t('leadDetail.scheduleMeeting')}
            </button>
          </div>
          {meetings.map(m => (
            <div className="card" key={m.meeting_id} style={{ marginBottom: 12 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
                <div style={{ minWidth: 0 }}>
                  <h3 style={{ fontSize: 16, marginBottom: 4, fontWeight: 700 }}>{m.title}</h3>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 8 }}>
                    <span className="badge badge-new">{m.meeting_type}</span>
                    <span className="badge badge-followup">{m.mode}</span>
                    <select
                      value={m.status || 'Scheduled'}
                      onChange={(e) => handleLeadMeetingStatusSelect(m, e.target.value)}
                      disabled={updatingMeetingStatusId === m.meeting_id}
                      style={{
                        minWidth: 130,
                        height: 28,
                        fontSize: 12,
                        fontWeight: 600,
                        borderRadius: 6,
                        border: '1px solid var(--input-border)',
                        color: 'var(--text-primary)',
                        background: 'var(--bg-primary)',
                        padding: '0 8px'
                      }}
                    >
                      {MEETING_STATUSES.map((s) => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  </div>
                  <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>
                    {new Date(m.meeting_date).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' })} at {m.meeting_time}
                    {m.timezone && ` (${m.timezone})`}
                    {m.location && ` | ${m.location}`}
                  </p>
                  {m.mode === 'Online' && m.meeting_link && (
                    <p style={{ fontSize: 13, marginTop: 6 }}>
                      <a href={m.meeting_link} target="_blank" rel="noreferrer" style={{ color: 'var(--primary)', fontWeight: 600 }}>{t('leadDetail.joinMeeting')}</a>
                      {m.zoom_meeting_id && (
                        <span style={{ marginLeft: 8, fontSize: 12, color: 'var(--text-muted)' }}>Zoom ID: {m.zoom_meeting_id}</span>
                      )}
                      {m.zoom_duration_minutes != null && (
                        <span style={{ marginLeft: 8, fontSize: 12, color: 'var(--text-muted)' }}>
                          Duration: {Number(m.zoom_duration_minutes)} min
                        </span>
                      )}
                    </p>
                  )}
                  {m.mode === 'Online' && m.zoom_meeting_id && (
                    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', marginTop: 6 }}>
                      {m.zoom_recording_url && (
                        <a href={m.zoom_recording_url} target="_blank" rel="noreferrer" style={{ fontSize: 12, color: '#059669', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                          <FiPlay size={12} /> Recording
                        </a>
                      )}
                      {m.zoom_transcript_url && (
                        <a href={m.zoom_transcript_url} target="_blank" rel="noreferrer" style={{ fontSize: 12, color: '#7c3aed', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                          <FiFileText size={12} /> Transcript
                        </a>
                      )}
                      {m.zoom_duration_minutes != null && (
                        <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                          {Number(m.zoom_duration_minutes)} min
                        </span>
                      )}
                      {zoomConfigured && (
                        <button
                          type="button"
                          title="Sync Zoom recordings"
                          className="btn btn-sm btn-outline"
                          style={{ padding: '2px 8px', fontSize: 11, display: 'inline-flex', alignItems: 'center', gap: 4 }}
                          disabled={zoomSyncing === m.meeting_id}
                          onClick={() => syncZoomOnLeadMeeting(m)}
                        >
                          <FiRefreshCw size={12} className={zoomSyncing === m.meeting_id ? 'spin' : ''} /> Sync
                        </button>
                      )}
                    </div>
                  )}
                  {m.mode === 'Online' && !m.zoom_meeting_id && zoomConfigured && (
                    <div style={{ marginTop: 8 }}>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline"
                        style={{ fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 4 }}
                        disabled={zoomGenerating === m.meeting_id}
                        onClick={() => generateZoomOnLeadCard(m)}
                      >
                        <FiVideo size={12} /> {zoomGenerating === m.meeting_id ? t('leadDetail.creatingZoom') : t('leadDetail.generateZoom')}
                      </button>
                    </div>
                  )}
                  {m.notes && <p style={{ marginTop: 4, fontSize: 13, color: 'var(--text-secondary)' }}>{m.notes}</p>}
                  {m.outcome && <p style={{ marginTop: 4, fontWeight: 600, color: 'var(--success)' }}>Outcome: {m.outcome}</p>}
                </div>
                {!m.outcome && m.status !== 'Completed' && (
                  <div style={{ flexShrink: 0 }}>
                    <select style={{
                      height: 32, fontSize: 12, border: '1px solid var(--input-border)',
                      borderRadius: 'var(--radius)', padding: '0 8px', fontFamily: 'var(--font)'
                    }}
                      defaultValue="" onChange={(e) => { if (e.target.value) handleUpdateOutcome(m.meeting_id, e.target.value); }}>
                      <option value="" disabled>{t('leadDetail.setOutcome')}</option>
                      <option value="Interested in applying">{t('leadDetail.outcomeInterested')}</option>
                      <option value="Need more information">{t('leadDetail.outcomeMoreInfo')}</option>
                      <option value="Follow-up required">{t('leadDetail.outcomeFollowUp')}</option>
                      <option value="Not interested">{t('leadDetail.outcomeNotInterested')}</option>
                      <option value="Ready for conversion">{t('leadDetail.outcomeReadyConversion')}</option>
                    </select>
                  </div>
                )}
                <div style={{ flexShrink: 0 }}>
                  <button className="btn btn-sm btn-outline" onClick={() => handleEditMeeting(m)}>
                    <FiEdit /> {t('common.edit')}
                  </button>
                </div>
              </div>
            </div>
          ))}
          {meetings.length === 0 && <div className="empty-state"><p>{t('leadDetail.noMeetings')}</p></div>}

          <HistoryDetails
            title={t('leadDetail.meetingsHistory')}
            logs={auditLog}
            filterLog={(l) => String(l?.entity_type || '').toLowerCase() === 'meeting'}
          />
        </div>
      )}

      {activeTab === 'emails' && (
        <div>
          <EmailThreadPanel entityType="lead" entityId={id} entityEmail={lead?.email} />
          <HistoryDetails
            title={t('leadDetail.emailsHistory')}
            logs={auditLog}
            filterLog={(l) => String(l?.action || '').toLowerCase().startsWith('email_')}
          />
        </div>
      )}

      {/* Agreements Tab (B2B only) */}
      {activeTab === 'agreements' && isB2B && (
        <div>
          <div style={{ marginBottom: 16, display: 'flex', gap: 8 }}>
            {canGenerateAgreement && (
              <button className="btn btn-primary btn-sm" onClick={openAgreementModal}>
                {t('leadDetail.generateAgreement')}
              </button>
            )}
            {canCreateAgreement && hasAnyAgreement && (
              <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                {t('leadDetail.agreementExists')}
              </div>
            )}
          </div>

          {agreements.length > 0 ? (
            agreements.map(agr => (
              <div className="card" key={agr.agreement_id} style={{ marginBottom: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                      <h3 style={{ fontSize: 16, fontWeight: 700, margin: 0 }}>Agreement #{agr.agreement_id}</h3>
                      <span style={{
                        padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600,
                        background:
                          agr.status === 'verified' ? 'var(--success-light)' :
                          agr.status === 'under_verification' ? 'var(--info-light)' :
                          agr.status === 'draft' ? 'var(--warning-light)' :
                          agr.status === 'not_verified' ? 'var(--danger-light)' :
                          'var(--danger-light)',
                        color:
                          agr.status === 'verified' ? 'var(--success)' :
                          agr.status === 'under_verification' ? 'var(--info)' :
                          agr.status === 'draft' ? 'var(--warning)' :
                          agr.status === 'not_verified' ? 'var(--danger)' :
                          'var(--danger)'
                      }}>
                        {agr.status.toUpperCase()}
                      </span>
                    </div>
                    <div style={{ marginBottom: 10, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                      <button className="btn btn-sm btn-outline" onClick={() => openEditAgreementModal(agr)}>
                        <FiEdit /> {t('leadDetail.editAgreement')}
                      </button>
                      {agr.status === 'draft' && (
                        <button
                          type="button"
                          className="btn btn-sm btn-outline"
                          onClick={() => openAgreementRegenerateModal(agr)}
                          style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
                        >
                          <FiRefreshCw size={14} aria-hidden />
                          {t('leadDetail.regenerateAgreementBtn')}
                        </button>
                      )}
                    </div>

                    <div className="detail-info-grid" style={{ gap: '8px 24px' }}>
                      <div className="detail-info-item">
                        <label>{t('leadDetail.agencyName')}</label>
                        <span>{agr.agency_name}</span>
                      </div>
                      <div className="detail-info-item">
                        <label>{t('leadDetail.agreementDate')}</label>
                        <span>{new Date(agr.agreement_date).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' })}</span>
                      </div>
                      <div className="detail-info-item">
                        <label>{t('leadDetail.directorName')}</label>
                        <span>{agr.director_name || '-'}</span>
                      </div>
                      <div className="detail-info-item">
                        <label>{t('leadDetail.companyName')}</label>
                        <span>{agr.company_name || '-'}</span>
                      </div>
                      <div className="detail-info-item">
                        <label>{t('leadDetail.companyEmail')}</label>
                        <span>{agr.company_email || '-'}</span>
                      </div>
                      <div className="detail-info-item">
                        <label>{t('leadDetail.companyAddress')}</label>
                        <span>{agr.company_address || '-'}</span>
                      </div>
                      {agr.created_by_name && (
                        <div className="detail-info-item">
                          <label>{t('common.createdBy')}</label>
                          <span>{agr.created_by_name}</span>
                        </div>
                      )}
                    </div>

                    {/* Downloads (always show when available) */}
                    {(agr.generated_file_path || agr.signed_file_path) && (
                      <div style={{ marginTop: 12, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                        {agr.generated_file_path && (
                          <button
                            type="button"
                            className="btn btn-sm btn-outline"
                            disabled={downloadingDraftAgreementId === agr.agreement_id || downloadingSignedAgreementId === agr.agreement_id}
                            onClick={() => handleDownloadGenerated(agr.agreement_id)}
                            style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
                          >
                            {downloadingDraftAgreementId === agr.agreement_id ? (
                              <>
                                <FiRefreshCw size={14} aria-hidden style={{ animation: 'spin 0.8s linear infinite' }} />
                                Preparing…
                              </>
                            ) : (
                              t('leadDetail.downloadDraft')
                            )}
                          </button>
                        )}
                        {agr.signed_file_path && (
                          <button
                            type="button"
                            className="btn btn-sm btn-outline"
                            disabled={downloadingSignedAgreementId === agr.agreement_id || downloadingDraftAgreementId === agr.agreement_id}
                            onClick={() => handleDownloadSigned(agr.agreement_id)}
                            style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
                          >
                            {downloadingSignedAgreementId === agr.agreement_id ? (
                              <>
                                <FiRefreshCw size={14} aria-hidden style={{ animation: 'spin 0.8s linear infinite' }} />
                                Preparing…
                              </>
                            ) : (
                              t('leadDetail.downloadSigned')
                            )}
                          </button>
                        )}
                      </div>
                    )}

                    {agr.status === 'draft' && (
                      <div style={{ marginTop: 12 }}>
                        <div style={{ marginTop: 8, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                          <input
                            type="file"
                            accept=".pdf,.jpg,.jpeg"
                            onChange={(e) => {
                              const f = e.target.files?.[0] || null;
                              if (f) {
                                const err = getDocumentFileSizeError(f);
                                if (err) {
                                  toast.error(err);
                                  e.target.value = '';
                                  return;
                                }
                              }
                              setSignedFiles((prev) => ({ ...prev, [agr.agreement_id]: f }));
                            }}
                          />
                          <button
                            className="btn btn-sm btn-primary"
                            disabled={uploadingSignedAgreementId === agr.agreement_id}
                            onClick={() => handleUploadSignedAgreement(agr.agreement_id, signedFiles[agr.agreement_id])}
                          >
                            {uploadingSignedAgreementId === agr.agreement_id ? t('common.uploading') : t('leadDetail.uploadSigned')}
                          </button>
                        </div>
                        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 6 }}>
                          {t('leadDetail.fileHint')}
                        </div>
                      </div>
                    )}

                    {agr.status === 'under_verification' && isAdmin && requiresVerification && (
                      <div style={{ marginTop: 12, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        <button
                          className="btn btn-sm btn-success"
                          onClick={async () => {
                            try {
                              await agencyAgreementsAPI.verify(agr.agreement_id, 'verified');
                              toast.success(t('leadDetail.toasts.verified'));
                              loadLead();
                            } catch (err) {
                              toast.error(err.response?.data?.message || t('leadDetail.toasts.verifyFailed'));
                            }
                          }}
                        >
                          {t('studentDetail.verify')}
                        </button>
                        <button
                          className="btn btn-sm btn-danger"
                          onClick={async () => {
                            try {
                              await agencyAgreementsAPI.verify(agr.agreement_id, 'not_verified');
                              toast.success(t('leadDetail.toasts.notVerified'));
                              loadLead();
                            } catch (err) {
                              toast.error(err.response?.data?.message || t('leadDetail.toasts.notVerifyFailed'));
                            }
                          }}
                        >
                          {t('studentDetail.notVerify')}
                        </button>
                      </div>
                    )}

                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="empty-state">
              <p>{t('leadDetail.noAgreements')}</p>
              {canCreateAgreement && (
                <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>
                  {t('leadDetail.createAgreementHint')}
                </p>
              )}
            </div>
          )}

          <HistoryDetails
            title={t('leadDetail.agreementsHistory')}
            logs={auditLog}
            filterLog={(l) => String(l?.entity_type || '').toLowerCase() === 'agreement'}
          />
        </div>
      )}

      {activeTab === 'notes' && !isB2B && (
        <div className="card">
          <h3 style={{ marginBottom: 12, fontWeight: 700 }}>{t('leadDetail.notesSection')}</h3>
          <p style={{ color: 'var(--text-secondary)' }}>{lead.remarks || t('leadDetail.noNotes')}</p>
        </div>
      )}

      {activeTab === 'history' && (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <h3 style={{ margin: '16px 20px 12px', fontWeight: 700 }}>{t('common.history')}</h3>
          <div style={{ padding: '0 0 12px' }}>
            <AuditTimeline logs={auditLog} showEntity />
          </div>
        </div>
      )}

      {/* Activity Modal */}
      {isFranceCRM && (
        <Modal
          isOpen={showStudentLinkLanguageModal}
          onClose={() => { if (!generatingStudentLink) setShowStudentLinkLanguageModal(false); }}
          title={t('students.studentFormLink')}
          footer={(
            <>
              <button className="btn btn-outline" onClick={() => setShowStudentLinkLanguageModal(false)} disabled={generatingStudentLink}>
                {t('common.cancel')}
              </button>
              <button
                className="btn btn-primary"
                onClick={() => handleGenerateStudentLinkForAgency(studentLinkLanguage)}
                disabled={generatingStudentLink}
              >
                {generatingStudentLink ? t('common.loading') : t('students.generateLink')}
              </button>
            </>
          )}
        >
          <div className="form-group" style={{ margin: 0 }}>
            <label style={{ marginBottom: 6, display: 'block', fontWeight: 600 }}>
              Form Language
            </label>
            <select
              value={studentLinkLanguage}
              onChange={(e) => setStudentLinkLanguage(e.target.value)}
              className="form-input"
            >
              <option value="en">{t('common.english', 'English')}</option>
              <option value="fr">{t('common.french', 'French')}</option>
            </select>
          </div>
        </Modal>
      )}

      <Modal isOpen={showActivityModal} onClose={() => setShowActivityModal(false)} title={t('leadDetail.activityModal')}
        footer={<><button className="btn btn-outline" onClick={() => setShowActivityModal(false)}>{t('common.cancel')}</button>
        <button className="btn btn-primary" onClick={handleLogActivity}>{t('leadDetail.saveActivity')}</button></>}>
        <div className="form-grid">
          <div className="form-group">
            <label>{t('leadDetail.activityTypeLabel')} <span className="required">*</span></label>
            <select value={activityForm.activity_type} onChange={(e) => setActivityForm(f => ({ ...f, activity_type: e.target.value }))}>
              {['Call', 'Email', 'WhatsApp', 'Meeting', 'Follow-up'].map(activityType => (
                <option key={activityType} value={activityType}>
                  {t(`common.activityType.${activityType}`, { defaultValue: activityType })}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>{t('leadDetail.dateLabel')} <span className="required">*</span></label>
            <input type="date" value={activityForm.activity_date} onChange={(e) => setActivityForm(f => ({ ...f, activity_date: e.target.value }))} />
          </div>
          <div className="form-group">
            <label>{t('leadDetail.timeLabel')}</label>
            <input type="time" value={activityForm.activity_time} onChange={(e) => setActivityForm(f => ({ ...f, activity_time: e.target.value }))} />
          </div>
          <div className="form-group">
            <label>{t('leadDetail.nextFollowupDate')}</label>
            <input type="date" value={activityForm.next_followup} onChange={(e) => setActivityForm(f => ({ ...f, next_followup: e.target.value }))} />
          </div>
          <div className="form-group full-width">
            <label>{t('leadDetail.notesLabel')} <span className="required">*</span></label>
            <textarea value={activityForm.notes} onChange={(e) => setActivityForm(f => ({ ...f, notes: e.target.value }))} placeholder={t('leadDetail.meetingNotesPlaceholder')} />
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={showEditAgreementModal}
        onClose={() => {
          if (editAgreementSaving) return;
          setShowEditAgreementModal(false);
          setEditingAgreement(null);
          setEditAgreementSignedFile(null);
        }}
        title={t('leadDetail.editAgreementTitle')}
        size="lg"
        footer={
          <>
            <button className="btn btn-outline" disabled={editAgreementSaving} onClick={() => setShowEditAgreementModal(false)}>{t('common.cancel')}</button>
            <button className="btn btn-primary" disabled={editAgreementSaving} onClick={handleSaveEditedAgreement}>
              {editAgreementSaving ? t('common.saving') : t('leadDetail.saveAgreement')}
            </button>
          </>
        }
      >
        <div className="form-grid">
          <div className="form-group">
            <label>{t('leadDetail.agreementDate')} <span className="required">*</span></label>
            <input type="date" value={agreementForm.agreement_date}
              onChange={(e) => setAgreementForm(f => ({ ...f, agreement_date: e.target.value }))} />
          </div>
          <div className="form-group">
            <label>{t('leadDetail.directorNameLabel')} <span className="required">*</span></label>
            <input type="text" value={agreementForm.director_name}
              onChange={(e) => setAgreementForm(f => ({ ...f, director_name: e.target.value }))} />
          </div>
          <div className="form-group">
            <label>{t('leadDetail.companyNameLabel')} <span className="required">*</span></label>
            <input type="text" value={agreementForm.company_name}
              onChange={(e) => setAgreementForm(f => ({ ...f, company_name: e.target.value }))} />
          </div>
          <div className="form-group">
            <label>{t('leadDetail.companyEmailLabel')} <span className="required">*</span></label>
            <input type="email" value={agreementForm.company_email}
              onChange={(e) => setAgreementForm(f => ({ ...f, company_email: e.target.value }))} />
          </div>
          <div className="form-group full-width">
            <label>{t('leadDetail.companyAddressLabel')} <span className="required">*</span></label>
            <textarea value={agreementForm.company_address}
              onChange={(e) => setAgreementForm(f => ({ ...f, company_address: e.target.value }))}
              rows={3} />
          </div>
          <div className="form-group full-width">
            <label>{t('leadDetail.reUploadSigned')}</label>
            <input
              type="file"
              accept=".pdf,.jpg,.jpeg"
              onChange={(e) => {
                const f = e.target.files?.[0] || null;
                if (f) {
                  const err = getDocumentFileSizeError(f);
                  if (err) { toast.error(err); e.target.value = ''; return; }
                }
                setEditAgreementSignedFile(f);
              }}
            />
            <small style={{ color: 'var(--text-muted)', display: 'block', marginTop: 6 }}>
              {requiresVerification
                ? 'If uploaded, agreement will move to Under Verification again. Max file size 25 MB (PDF, JPG, JPEG).'
                : 'If uploaded, agreement will be auto-verified and agency Tied-Up. Max file size 25 MB (PDF, JPG, JPEG).'}
            </small>
          </div>
        </div>
      </Modal>

      {/* Meeting Modal */}
      <Modal isOpen={showMeetingModal} onClose={() => { setShowMeetingModal(false); setEditingMeeting(null); setInitialLeadMeetingSlot(null); }} title={editingMeeting ? t('leadDetail.editMeetingModal') : t('leadDetail.meetingModal')} size="lg"
        footer={<><button className="btn btn-outline" onClick={() => setShowMeetingModal(false)}>{t('common.cancel')}</button>
        <button className="btn btn-primary" onClick={handleScheduleMeeting}>{editingMeeting ? t('common.update') : t('leadDetail.meetingModal')}</button></>}>
        <div className="form-grid">
          <div className="form-group full-width">
            <label>{t('leadDetail.meetingTitle')} <span className="required">*</span></label>
            <input type="text" value={meetingForm.title} onChange={(e) => setMeetingForm(f => ({ ...f, title: e.target.value }))} placeholder={t('leadDetail.meetingTitlePlaceholder')} />
          </div>
          <div className="form-group">
            <label>{t('leadDetail.meetingType')} <span className="required">*</span></label>
            <select value={meetingForm.meeting_type} onChange={(e) => setMeetingForm(f => ({ ...f, meeting_type: e.target.value }))}>
              {meetingTypes.length > 0
                ? meetingTypes.map(t => <option key={t.meeting_type_id || t.type_name} value={t.type_name}>{t.type_name}</option>)
                : ['Agency Partnership'].map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label>{t('leadDetail.meetingMode')} <span className="required">*</span></label>
            <select value={meetingForm.mode} onChange={(e) => setMeetingForm(f => ({ ...f, mode: e.target.value }))}>
              <option value="Online">{t('leadDetail.meetingOnline')}</option>
              <option value="Offline">{t('leadDetail.meetingOffline')}</option>
            </select>
          </div>
          <div className="form-group">
            <label>{t('leadDetail.meetingDate')} <span className="required">*</span></label>
            <input
              type="date"
              value={meetingForm.meeting_date}
              onChange={(e) => setMeetingForm(f => ({ ...f, meeting_date: e.target.value }))}
              min={editingMeeting ? undefined : getTodayDateInputMin()}
            />
          </div>
          <div className="form-group">
            <label>{t('leadDetail.meetingTime')} <span className="required">*</span></label>
            <input type="time" value={meetingForm.meeting_time} onChange={(e) => setMeetingForm(f => ({ ...f, meeting_time: e.target.value }))} />
          </div>
          <div className="form-group">
            <label>{t('leadDetail.meetingTimezone')} <span className="required">*</span></label>
            <select value={meetingForm.timezone} onChange={(e) => setMeetingForm(f => ({ ...f, timezone: e.target.value }))}>
              {['Asia/Colombo', 'Asia/Dubai', 'Europe/Paris', 'Europe/London', 'Asia/Kolkata', 'UTC'].map(tz => (
                <option key={tz} value={tz}>{tz}</option>
              ))}
            </select>
          </div>
          {meetingForm.mode === 'Online' && (
            <div className="form-group full-width">
              <label>{t('leadDetail.meetingLink')}</label>
              <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: '0 0 8px' }}>
                {t('leadDetail.meetingLinkDesc')}
              </p>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <input
                  type="url"
                  style={{ flex: 1, minWidth: 200 }}
                  value={meetingForm.meeting_link}
                  onChange={(e) => setMeetingForm(f => ({ ...f, meeting_link: e.target.value }))}
                  placeholder={t('leadDetail.meetingLinkPlaceholder')}
                />
                {zoomConfigured && !meetingForm.zoom_meeting_id && (
                  <button
                    type="button"
                    className="btn btn-outline btn-sm"
                    style={{ whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: 6 }}
                    disabled={zoomGeneratingModal}
                    onClick={generateZoomInLeadMeetingModal}
                  >
                    <FiVideo size={14} /> {zoomGeneratingModal ? t('leadDetail.creatingZoom') : t('leadDetail.generateZoom')}
                  </button>
                )}
              </div>
              {meetingForm.zoom_meeting_id && (
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 6 }}>
                  Zoom meeting ID: {meetingForm.zoom_meeting_id}
                  {meetingForm.zoom_passcode ? ` · Passcode: ${meetingForm.zoom_passcode}` : ''}
                </div>
              )}
            </div>
          )}
          {meetingForm.mode === 'Offline' && (
            <div className="form-group full-width">
              <label>{t('leadDetail.meetingLocation')} <span className="required">*</span></label>
              <input type="text" value={meetingForm.location} onChange={(e) => setMeetingForm(f => ({ ...f, location: e.target.value }))} placeholder={t('leadDetail.meetingLocationPlaceholder')} />
            </div>
          )}
          <div className="form-group full-width">
            <label>{t('leadDetail.meetingNotes')}</label>
            <textarea value={meetingForm.notes} onChange={(e) => setMeetingForm(f => ({ ...f, notes: e.target.value }))} placeholder={t('leadDetail.meetingNotesPlaceholder')} />
          </div>
          {editingMeeting && (
            <div className="form-group">
              <label>{t('leadDetail.meetingStatus')}</label>
              <select value={meetingForm.status || 'Scheduled'} onChange={(e) => setMeetingForm(f => ({ ...f, status: e.target.value }))}>
                {['Scheduled', 'Completed', 'Cancelled'].map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
              <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: '6px 0 0' }}>
                To reschedule (new time/link), set status to <strong>Rescheduled</strong> from the meeting card above.
              </p>
            </div>
          )}
        </div>
      </Modal>

      {/* Reschedule — opened when status → Rescheduled on meeting card */}
      <Modal
        isOpen={showRescheduleModal && !!rescheduleForm && !!rescheduleMeeting}
        onClose={closeLeadRescheduleModal}
        title={t('leadDetail.rescheduleTitle')}
        size="lg"
        footer={
          <>
            <button type="button" className="btn btn-outline" onClick={closeLeadRescheduleModal}>{t('common.cancel')}</button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleLeadRescheduleSubmit}
              disabled={updatingMeetingStatusId === rescheduleMeeting?.meeting_id}
            >
              {updatingMeetingStatusId === rescheduleMeeting?.meeting_id ? t('common.saving') : t('leadDetail.rescheduleTitle')}
            </button>
          </>
        }
      >
        {rescheduleMeeting && rescheduleForm && (
          <div className="form-grid">
            <div className="form-group full-width">
              <label>Meeting</label>
              <p style={{ margin: 0, fontWeight: 600 }}>{rescheduleMeeting.title}</p>
              <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: '6px 0 0' }}>
                {t('leadDetail.rescheduleDesc')}
              </p>
            </div>
            <div className="form-group">
              <label>{t('leadDetail.newDate')} <span className="required">*</span></label>
              <input
                type="date"
                value={rescheduleForm.meeting_date}
                onChange={(e) => setRescheduleForm((f) => ({ ...f, meeting_date: e.target.value }))}
                min={getTodayDateInputMin()}
              />
            </div>
            <div className="form-group">
              <label>{t('leadDetail.newTime')} <span className="required">*</span></label>
              <input
                type="time"
                value={rescheduleForm.meeting_time}
                onChange={(e) => setRescheduleForm((f) => ({ ...f, meeting_time: e.target.value }))}
              />
            </div>
            <div className="form-group">
              <label>{t('leadDetail.meetingTimezone')} <span className="required">*</span></label>
              <select
                value={rescheduleForm.timezone || resolveDefaultTimezoneFromList(TIME_ZONES)}
                onChange={(e) => setRescheduleForm((f) => ({ ...f, timezone: e.target.value }))}
              >
                {TIME_ZONES.map((tz) => (
                  <option key={tz} value={tz}>{tz}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>{t('leadDetail.meetingMode')}</label>
              <select
                value={rescheduleForm.mode}
                onChange={(e) => setRescheduleForm((f) => ({ ...f, mode: e.target.value }))}
              >
                {MEETING_MODES.map((mod) => (
                  <option key={mod} value={mod}>{mod}</option>
                ))}
              </select>
            </div>
            {rescheduleForm.mode === 'Offline' && (
              <div className="form-group full-width">
                <label>{t('leadDetail.meetingLocation')} <span className="required">*</span></label>
                <input
                  value={rescheduleForm.location}
                  onChange={(e) => setRescheduleForm((f) => ({ ...f, location: e.target.value }))}
                  placeholder={t('leadDetail.rescheduleLocation')}
                />
              </div>
            )}
            {rescheduleForm.mode === 'Online' && (
              <div className="form-group full-width">
                <label>{t('leadDetail.meetingLink')}</label>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                  <input
                    style={{ flex: 1, minWidth: 200 }}
                    value={rescheduleForm.meeting_link}
                    onChange={(e) => setRescheduleForm((f) => ({ ...f, meeting_link: e.target.value }))}
                    placeholder={t('leadDetail.meetingLinkPlaceholder')}
                  />
                  {zoomConfigured && (
                    <button
                      type="button"
                      className="btn btn-outline btn-sm"
                      style={{ whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: 6 }}
                      disabled={zoomGeneratingReschedule}
                      onClick={generateZoomInLeadRescheduleModal}
                    >
                      <FiVideo size={14} /> {zoomGeneratingReschedule ? t('leadDetail.working') : t('leadDetail.generateZoom')}
                    </button>
                  )}
                </div>
                {rescheduleForm.zoom_meeting_id && (
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 6 }}>
                    Zoom meeting ID: {rescheduleForm.zoom_meeting_id}
                    {rescheduleForm.zoom_passcode ? ` · Passcode: ${rescheduleForm.zoom_passcode}` : ''}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* Agreement Modal (B2B) */}
      <Modal
        isOpen={showAgreementModal}
        onClose={() => { setShowAgreementModal(false); setRegenerateAgreementId(null); }}
        title={t('leadDetail.generateAgreementTitle')}
        size="lg"
        footer={
          <>
            <button className="btn btn-outline" onClick={() => {
              if (selectedAgrTemplate && !generatedAgreement) {
                setSelectedAgrTemplate(null);
                setAgrTemplateFields([]);
                setAgrFieldValues({});
                return;
              }
              setShowAgreementModal(false);
            }}>
              {selectedAgrTemplate && !generatedAgreement ? t('common.back') : t('common.cancel')}
            </button>
            {!generatedAgreement ? (
              (selectedAgrTemplate || agreementTemplates.length === 0) && (
                <button
                  className="btn btn-primary"
                  disabled={agreementGenerating}
                  onClick={handleGenerateAgreement}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}
                >
                  {agreementGenerating ? (
                    <>
                      <FiRefreshCw size={16} aria-hidden style={{ animation: 'spin 0.8s linear infinite' }} />
                      {t('leadDetail.generating')}
                    </>
                  ) : (
                    regenerateAgreementId ? t('leadDetail.regenerateAgreementBtn') : t('leadDetail.generateAgreementBtn')
                  )}
                </button>
              )
            ) : (
              <button
                className="btn btn-primary"
                disabled={uploadingSignedAgreementId === generatedAgreement.agreement_id}
                onClick={() => handleUploadSignedAgreement(generatedAgreement.agreement_id, modalSignedFile)}
              >
                {uploadingSignedAgreementId === generatedAgreement.agreement_id ? t('common.uploading') : t('leadDetail.uploadSignedAgreement')}
              </button>
            )}
          </>
        }
      >
        {!generatedAgreement ? (
          <>
            {regenerateAgreementId && (
              <div style={{ marginBottom: 12, padding: 12, background: 'var(--warning-light)', borderRadius: 'var(--radius)', fontSize: 13, border: '1px solid var(--warning)' }}>
                {t('leadDetail.regenerateAgreementBanner')}
              </div>
            )}
            <div style={{ marginBottom: 16, padding: 12, background: 'var(--info-light)', borderRadius: 'var(--radius)', fontSize: 13 }}>
              {t('leadDetail.creatingAgreementFor', { name: lead.student_name })}
              {selectedAgrTemplate
                ? ` ${t('leadDetail.usingTemplate', { name: selectedAgrTemplate.template_name })}`
                : agreementTemplates.length > 0
                  ? ` ${t('leadDetail.selectTemplateHint')}`
                  : ` ${t('leadDetail.fillDetails')}`}
            </div>

            {/* Step 1: Template picker (if templates exist and none selected) */}
            {agreementTemplates.length > 0 && !selectedAgrTemplate && (
              <div>
                <h4 style={{ marginBottom: 12, fontWeight: 600 }}>{t('leadDetail.chooseTemplate')}</h4>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 10 }}>
                  {agreementTemplates.map(tpl => (
                    <div key={tpl.template_id} style={{
                      border: '1px solid var(--border)', borderRadius: 10, padding: 14,
                      cursor: 'pointer', transition: 'all 0.15s',
                      background: 'var(--white)'
                    }}
                      onClick={() => handleSelectAgrTemplate(tpl)}
                      onMouseEnter={e => { e.currentTarget.style.borderColor = '#4f46e5'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(79,70,229,0.12)'; }}
                      onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.boxShadow = 'none'; }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                        <FiFileText style={{ color: '#4f46e5' }} />
                        <span style={{ fontWeight: 600, fontSize: 14 }}>{tpl.template_name}</span>
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                        {(tpl.fields || []).slice(0, 4).map(f => (
                          <span key={f.field_id} style={{
                            padding: '1px 6px', borderRadius: 4, fontSize: 10,
                            background: f.auto_map ? '#dcfce7' : '#fef3c7',
                            color: f.auto_map ? '#166534' : '#92400e'
                          }}>{f.field_label}</span>
                        ))}
                        {(tpl.fields || []).length > 4 && (
                          <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>+{(tpl.fields || []).length - 4} more</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Step 2: Template fields form (auto-filled) */}
            {selectedAgrTemplate && (
              <div className="form-grid" style={{ gridTemplateColumns: '1fr' }}>
                {agrTemplateFields.map(f => {
                  const kind = getTemplateFieldInputKind(f.field_key);
                  const inputStyle = { color: 'var(--text)', background: 'var(--white)' };
                  return (
                  <div className="form-group" key={f.field_key}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--text)' }}>
                      {f.field_label}
                      {f.auto_map && f.value && (
                        <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 4, background: '#dcfce7', color: '#166534' }}>
                          <FiCheck size={9} style={{ marginRight: 2 }} />{t('leadDetail.autoFilled')}
                        </span>
                      )}
                    </label>
                    {kind === 'date' ? (
                      <input
                        type="date"
                        value={agrFieldValues[f.field_key] || ''}
                        onChange={e => setAgrFieldValues(prev => ({ ...prev, [f.field_key]: e.target.value }))}
                        style={inputStyle}
                      />
                    ) : kind === 'textarea' ? (
                      <textarea
                        value={agrFieldValues[f.field_key] || ''}
                        onChange={e => setAgrFieldValues(prev => ({ ...prev, [f.field_key]: e.target.value }))}
                        placeholder={`Enter ${f.field_label}`}
                        rows={4}
                        style={{ ...inputStyle, minHeight: 88, resize: 'vertical' }}
                      />
                    ) : (
                      <input
                        type="text"
                        value={agrFieldValues[f.field_key] || ''}
                        onChange={e => setAgrFieldValues(prev => ({ ...prev, [f.field_key]: e.target.value }))}
                        placeholder={`Enter ${f.field_label}`}
                        style={inputStyle}
                      />
                    )}
                  </div>
                  );
                })}
                {agrTemplateFields.length === 0 && (
                  <p style={{ color: 'var(--text-muted)', textAlign: 'center', padding: 12 }}>
                    {t('leadDetail.noFieldsTemplate')}
                  </p>
                )}
              </div>
            )}

            {/* Legacy form (no templates available) */}
            {agreementTemplates.length === 0 && (
              <div className="form-grid">
                <div className="form-group">
                  <label>{t('leadDetail.agreementDate')} <span className="required">*</span></label>
                  <input type="date" value={agreementForm.agreement_date}
                    onChange={(e) => setAgreementForm(f => ({ ...f, agreement_date: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label>{t('leadDetail.directorNameLabel')} <span className="required">*</span></label>
                  <input type="text" value={agreementForm.director_name}
                    onChange={(e) => setAgreementForm(f => ({ ...f, director_name: e.target.value }))}
                    placeholder={t('leadDetail.directorPlaceholder')} />
                </div>
                <div className="form-group">
                  <label>{t('leadDetail.companyNameLabel')} <span className="required">*</span></label>
                  <input type="text" value={agreementForm.company_name}
                    onChange={(e) => setAgreementForm(f => ({ ...f, company_name: e.target.value }))}
                    placeholder={t('leadDetail.companyPlaceholder')} />
                </div>
                <div className="form-group">
                  <label>{t('leadDetail.companyEmailLabel')} <span className="required">*</span></label>
                  <input type="email" value={agreementForm.company_email}
                    onChange={(e) => setAgreementForm(f => ({ ...f, company_email: e.target.value }))}
                    placeholder={t('leadDetail.companyEmailPlaceholder')} />
                </div>
                <div className="form-group full-width">
                  <label>{t('leadDetail.companyAddressLabel')} <span className="required">*</span></label>
                  <textarea value={agreementForm.company_address}
                    onChange={(e) => setAgreementForm(f => ({ ...f, company_address: e.target.value }))}
                    placeholder={t('leadDetail.companyAddressPlaceholder')}
                    rows={3} />
                </div>
              </div>
            )}
          </>
        ) : (
          <>
            <div style={{ marginBottom: 16, padding: 12, background: 'var(--success-light)', borderRadius: 'var(--radius)', fontSize: 13 }}>
              {t('leadDetail.agreementGenerated')}
            </div>
            <div style={{ marginBottom: 12, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              <button
                type="button"
                className="btn btn-outline"
                onClick={() => window.open(generatedAgreement.generated_url, '_blank', 'noopener,noreferrer')}
              >
                {t('leadDetail.downloadGenerated')}
              </button>
              <button
                type="button"
                className="btn btn-outline"
                disabled={agreementGenerating}
                onClick={() => {
                  setRegenerateAgreementId(generatedAgreement.agreement_id);
                  setGeneratedAgreement(null);
                  setModalSignedFile(null);
                }}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
              >
                <FiRefreshCw size={16} aria-hidden />
                {t('leadDetail.regenerateAgreementBtn')}
              </button>
              <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                ID: {generatedAgreement.agreement_id}
              </span>
            </div>
            <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: '0 0 12px' }}>
              {t('leadDetail.regenerateAgreementHint')}
            </p>
            <div className="form-group">
              <label>{t('leadDetail.uploadSignedLabel')} <span className="required">*</span></label>
              <input
                type="file"
                accept=".pdf,.jpg,.jpeg"
                onChange={(e) => {
                  const f = e.target.files?.[0] || null;
                  if (f) {
                    const err = getDocumentFileSizeError(f);
                    if (err) { toast.error(err); e.target.value = ''; return; }
                  }
                  setModalSignedFile(f);
                }}
              />
              <small style={{ color: 'var(--text-muted)', display: 'block', marginTop: 6 }}>
                {t('leadDetail.fileHint')}
              </small>
            </div>
          </>
        )}
      </Modal>
    </div>
  );
};

export default LeadDetail;
